import React from 'react';
import { FullScreenHandle } from 'react-full-screen';
import { useTranslation } from 'react-i18next';
import { match, P } from 'ts-pattern';
import { MetricDetails } from '../../../api/metrics/types';
import { ApiOverTimeTimeSegmentType, DataValue } from '../../../api/types';
import { toTimeSegmentLabel } from '../../../api/utils';
import { UNDEFINED_DISPLAY_KEY } from '../../../constants';
import {
  useGlobalLocaleContext,
  useMetricDetailsMapContext,
} from '../../../context/contexts';
import { DataFieldWithDataType, MetricIdType } from '../../../types';
import {
  formatMetricValue,
  formatPercentageNumber,
} from '../../../utils-display';
import { useGetMetricDisplayName } from '../charts/hooks';
import { formatHierarchicalLabel } from '../filter/utils-display';
import { TableView } from './TableView';
import {
  MetricTableHeader,
  TableData,
  TableMetricsData,
  TableViewHeader,
  TableViewRow,
} from './types';

export const TableViewMetrics = <T extends MetricIdType>({
  tableData,
  fullScreenHandle,
  chartHeight,
  showPercentage = false,
}: {
  tableData: TableMetricsData<T>;
  fullScreenHandle: FullScreenHandle;
  chartHeight: string;
  showPercentage?: boolean;
}) => {
  const { t } = useTranslation();
  const metricDetailsMap = useMetricDetailsMapContext();
  const locale = useGlobalLocaleContext();
  const getMetricDisplayName = useGetMetricDisplayName();
  const getColumnCellDataFromHeader = (header: MetricTableHeader<T>) => {
    const columnCellData: TableViewHeader = match(header)
      .with({ type: 'blank' }, () => {
        return { value: '' };
      })
      .with(
        P.union({ type: 'non-segmented' }, { type: 'segmented-by-metric' }),
        (h) => {
          const metricDetails = metricDetailsMap[h.metricId.value];
          return {
            value: getMetricDisplayName(
              h.metricId.value,
              metricDetails?.metricGroupId
            ),
          };
        }
      )
      .with({ type: 'segmented-by-datafield' }, (h) => {
        return {
          value: formatHierarchicalLabel(
            h.value.value,
            h.value.dataField,
            t(UNDEFINED_DISPLAY_KEY),
            t
          ),
        };
      })
      .otherwise(() => {
        throw new Error('Invalid header value');
      });
    return columnCellData;
  };
  const headers: TableViewHeader[] = tableData.headers.map((header) => {
    return getColumnCellDataFromHeader(header);
  });
  const rows: TableViewRow[] = tableData.rows.map((row) => {
    const rowTitle: string = match(row.title)
      .with(
        P.union(
          { __typename: 'CalendarYearMonthlySegment' },
          { __typename: 'CalendarYearQuarterlySegment' },
          { __typename: 'CalendarYearYearlySegment' },
          { __typename: 'FinancialYearQuarterlySegment' },
          { __typename: 'FinancialYearYearlySegment' }
        ),
        (timeSegment: ApiOverTimeTimeSegmentType) =>
          toTimeSegmentLabel(timeSegment, locale.selected)
      )
      .with(
        { value: P.array(P.union(P.string, P.nullish)) },
        (title: { value: DataValue[]; dataField: DataFieldWithDataType }) => {
          return formatHierarchicalLabel(
            title.value,
            title.dataField,
            t(UNDEFINED_DISPLAY_KEY),
            t
          );
        }
      )
      .otherwise(() => {
        throw new Error('Invalid row title');
      });
    const cells = row.cells.map((cell) => {
      return match(cell)
        .with({ type: 'table-cell-empty' }, () => {
          return { value: '' };
        })
        .with({ type: 'table-cell-metric' }, (cell) => {
          const metricDetails: MetricDetails =
            metricDetailsMap[cell.metricId.value];
          const formatType = metricDetails?.formatType;
          const formattedValue = showPercentage
            ? cell.value
              ? formatPercentageNumber(cell.value)
              : '0%'
            : formatMetricValue(cell.value, formatType);
          return { value: formattedValue };
        })
        .with({ type: 'table-cell-time' }, (cell) => {
          return { value: toTimeSegmentLabel(cell.value, locale.selected) };
        })
        .exhaustive();
    });
    return { title: rowTitle, cells };
  });
  const tableViewData: TableData = {
    rows,
    headers,
  };
  return (
    <TableView
      tableData={tableViewData}
      fullScreenHandle={fullScreenHandle}
      chartHeight={chartHeight}
    />
  );
};
